import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Check, ArrowRight, ArrowLeft, ArrowDown, CalendarCheck, Sparkles,
  Loader2, ShieldCheck, Lock, Zap, Info, Palette,
} from "lucide-react";
import { BASES, ADDONS, baseById, addonById } from "@/config/clinicOS";
import { attributionPayload } from "@/access/tracker";
import { PricingNote } from "@/components/landing/PricingNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMR_OPTIONS = [
  "Jane App", "Juvonno", "ClinicSense", "PracticeQ", "Cliniko", "Other", "No EMR yet",
];

const CLINIC_TYPES = [
  "Physiotherapy", "Chiropractic", "Dental", "Med-Spa / Aesthetic",
  "Dietetics / Nutrition", "Mental Health", "Multi-disciplinary", "Other",
];

const PRACTITIONER_COUNTS = ["1", "2 – 5", "6 – 15", "16 – 30", "30+"];

/* 4-step Start Plan flow — mobile-first.
   - Mobile: full-screen sheet, icon-only stepper, sticky bottom nav
   - Desktop: floating card with full stepper labels
   - Add-on details: bottom-sheet on mobile, right-drawer on desktop
   Pricing total intentionally shows base + whitelabel only; add-ons are
   explicitly disclosed as "quoted after the discovery call". */
export const StartPlanModal = ({ baseId, onClose }) => {
  const [activeBaseId, setActiveBaseId] = useState(baseId || "hub");
  const [step, setStep] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [whitelabel, setWhitelabel] = useState(false);
  const [drawerAddonId, setDrawerAddonId] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", clinicName: "", phone: "",
    emr: "", clinicType: "", practitioners: "", websiteUrl: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitState, setSubmitState] = useState("idle");
  const [error, setError] = useState("");

  const base = useMemo(() => baseById(activeBaseId) || BASES[0], [activeBaseId]);
  const otherBase = useMemo(() => BASES.find((b) => b.id !== base.id), [base.id]);
  const drawerAddon = useMemo(() => addonById(drawerAddonId), [drawerAddonId]);

  // Base + whitelabel only — add-ons are NEVER auto-totaled (custom-quoted).
  const baseTotal = useMemo(() => {
    const wl = whitelabel ? 297 : 0;
    return base.price + wl;
  }, [base.price, whitelabel]);

  const addonCount = selectedAddons.size;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (drawerAddonId) setDrawerAddonId(null);
        else onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, drawerAddonId]);

  const isAddonAvailable = (a) => !a.requiresBase || a.requiresBase === activeBaseId;

  const toggleAddon = (id) => {
    const a = addonById(id);
    if (!a) return;
    if (!isAddonAvailable(a)) {
      setDrawerAddonId(id);
      return;
    }
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name.trim())       errors.name       = "Required";
    if (!form.clinicName.trim()) errors.clinicName = "Required";
    if (!form.email.trim())      errors.email      = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Enter a valid email";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError("Please fill in the highlighted fields.");
      setSubmitState("error");
      const firstKey = ["name", "clinicName", "email"].find((k) => errors[k]);
      if (firstKey) {
        const el = document.querySelector(`[data-testid="start-form-${firstKey === "clinicName" ? "clinic" : firstKey}"]`);
        if (el && typeof el.focus === "function") el.focus();
      }
      return;
    }

    setFieldErrors({});
    setSubmitState("loading");
    setError("");

    const addonsList = Array.from(selectedAddons).map((id) => addonById(id)?.name).filter(Boolean);
    const whitelabelLine = whitelabel ? " + Whitelabel ($297/mo)" : "";
    const addonsLine = addonsList.length
      ? ` + Add-ons (quoted after discovery): ${addonsList.join(", ")}`
      : " · No add-ons";
    const message = `Selected: ${base.name} (${base.priceLabel}/mo · fixed)${addonsLine}${whitelabelLine}. Base monthly: $${baseTotal}/mo. Add-on pricing custom.`;

    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        clinic_name: form.clinicName.trim(),
        phone: form.phone.trim() || "—",
        clinic_type: form.clinicType || undefined,
        emr: form.emr || undefined,
        practitioners: form.practitioners || undefined,
        website_url: form.websiteUrl.trim() || undefined,
        message,
        source: "build-os",
        selected_base:       base.name,
        selected_base_price: base.price,
        selected_addons:     addonsList,
        whitelabel:          !!whitelabel,
        total_monthly:       baseTotal, // base + whitelabel ONLY
        attribution:         attributionPayload({ page: "/build" }),
      });
      setSubmitState("success");
    } catch (err) {
      setSubmitState("error");
      setError(err?.response?.data?.detail?.[0]?.msg || "Couldn't submit — please try again.");
    }
  };

  const featuredColor = base.id === "hub-plus" ? "violet" : "blue";
  const COLOR = {
    violet: { ring: "ring-violet-500/45", border: "border-violet-500/35", bg: "bg-violet-500/10", text: "text-violet-600 dark:text-violet-300", btn: "from-blue-600 to-violet-600", grad: "from-violet-500/[0.10] via-blue-500/[0.04]" },
    blue:   { ring: "ring-blue-500/45",   border: "border-blue-500/35",   bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-300",   btn: "from-blue-600 to-blue-500",  grad: "from-blue-500/[0.10] via-blue-500/[0.04]" },
  }[featuredColor];

  const STEPS = ["Review", "Add-ons", "Whitelabel", "Schedule"];

  const handleUpgradeBase = () => {
    const targetId = otherBase.id;
    setActiveBaseId(targetId);
    setSelectedAddons((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        const a = addonById(id);
        if (!a) return;
        if (!a.requiresBase || a.requiresBase === targetId) next.add(id);
      });
      return next;
    });
  };

  const handleAddFromDrawer = () => {
    if (!drawerAddon) return;
    if (!isAddonAvailable(drawerAddon)) {
      setActiveBaseId(otherBase.id);
    }
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      next.add(drawerAddon.id);
      return next;
    });
    setDrawerAddonId(null);
  };

  const handleRemoveFromDrawer = () => {
    if (!drawerAddon) return;
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      next.delete(drawerAddon.id);
      return next;
    });
    setDrawerAddonId(null);
  };

  return (
    <div
      data-testid="start-plan-modal"
      className="fixed inset-0 z-[110] flex items-stretch justify-center sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/45 backdrop-blur-sm" />

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-[100dvh] max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-card shadow-2xl sm:h-[780px] sm:max-h-[96vh] sm:rounded-2xl sm:border sm:border-border"
      >
        {/* Top accent */}
        <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500" />

        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-border/70 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-2 pr-8">
            <Sparkles className={`h-4 w-4 flex-none ${COLOR.text}`} />
            <p className="truncate font-display text-[14px] font-bold tracking-tight sm:text-[15px]">
              Start with {base.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="start-plan-modal-close"
            aria-label="Close"
            className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground sm:right-4 sm:top-4 sm:h-8 sm:w-8"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* MOBILE Stepper — icon dots + current label */}
        <div className="border-b border-border/70 px-4 py-3 sm:hidden">
          <div className="flex items-center gap-2">
            {STEPS.map((label, i) => {
              const idx = i + 1;
              const active = step === idx;
              const done = step > idx;
              return (
                <div key={label} className="flex flex-1 items-center gap-1">
                  <span className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-bold transition-all ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"
                  }`}>
                    {done ? <Check className="h-3 w-3" /> : idx}
                  </span>
                  {i < STEPS.length - 1 && (
                    <span className={`h-px flex-1 ${done ? "bg-emerald-500" : "bg-border"}`} />
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Step {step} of 4 · <span className="text-foreground">{STEPS[step - 1]}</span>
          </p>
        </div>

        {/* DESKTOP Stepper — full labels */}
        <div className="hidden items-center justify-between gap-3 border-b border-border/70 px-6 py-3 sm:flex">
          {STEPS.map((label, i) => {
            const idx = i + 1;
            const active = step === idx;
            const done = step > idx;
            return (
              <div key={label} className="flex flex-1 items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-bold transition-all ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"
                  }`}>
                    {done ? <Check className="h-3 w-3" /> : idx}
                  </span>
                  <span className={`text-[12px] font-semibold tracking-tight ${active ? "text-foreground" : "text-muted-foreground"}`}>
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <span className={`h-px flex-1 ${done ? "bg-emerald-500" : "bg-border"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Body — scroll area */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {/* STEP 1 — REVIEW */}
          {step === 1 && (
            <div data-testid="start-step-review">
              <div className={`relative overflow-hidden rounded-2xl border ${COLOR.border} bg-gradient-to-br ${COLOR.grad} to-transparent p-4 sm:p-5`}>
                <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/15 blur-3xl" />
                <div className="relative flex items-start gap-3">
                  <span className={`inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${base.color} text-white shadow-md ring-1 ring-inset ring-white/15 sm:h-12 sm:w-12`}>
                    {(() => { const I = base.icon; return <I className="h-5 w-5" />; })()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg font-black tracking-tight sm:text-xl">{base.name}</p>
                    <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{base.tagline}</p>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-display text-2xl font-black tracking-tight">{base.priceLabel}</span>
                      <span className="text-[12px] text-muted-foreground">/month · per location</span>
                    </div>
                    <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300">
                      <ShieldCheck className="h-2.5 w-2.5" />
                      Fixed price · no surprises
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing transparency — sits prominently right after price */}
              <PricingNote className="mt-3" variant="long" />

              <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">What&apos;s included</p>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {base.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] leading-snug text-foreground/85">
                    <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${COLOR.text}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 rounded-xl border border-border bg-secondary/40 p-3.5 text-[12px]">
                <div className="flex items-center gap-2 text-foreground/85">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="font-semibold">Plus on every plan:</span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  Unlimited Practitioners · Unlimited Team Users · Unlimited Patients · Unlimited Support · Live in 14 days.
                </p>
              </div>

              {/* Base switcher CTA */}
              {(() => {
                const isUpgrade = otherBase.id === "hub-plus";
                return (
                  <div
                    className={`mt-5 flex flex-col items-stretch gap-3 rounded-xl border p-3.5 sm:flex-row sm:items-center ${
                      isUpgrade
                        ? "border-violet-500/35 bg-gradient-to-br from-violet-500/[0.08] via-blue-500/[0.04] to-transparent"
                        : "border-blue-500/35 bg-gradient-to-br from-blue-500/[0.08] via-blue-500/[0.04] to-transparent"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${otherBase.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                        {(() => { const I = otherBase.icon; return <I className="h-4 w-4" />; })()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            {isUpgrade ? "Step up to" : "Step down to"}
                          </span>
                          {isUpgrade && (
                            <span className="rounded-sm bg-violet-500 px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.1em] text-white">Most popular</span>
                          )}
                        </div>
                        <p className="text-[13.5px] font-bold tracking-tight">
                          {otherBase.name} · <span className="font-display">{otherBase.priceLabel}/mo</span>
                        </p>
                        <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                          {isUpgrade
                            ? "Unlock Social Suite, Automations, Call IQ + all add-ons available."
                            : "Skip Social Suite & Automations to save $200/mo. Add specialty modules later."}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleUpgradeBase}
                      data-testid="start-base-switch"
                      className={`group inline-flex h-11 w-full flex-none items-center justify-center gap-1.5 rounded-xl px-4 text-[12.5px] font-bold transition-all hover:-translate-y-0.5 sm:w-auto ${
                        isUpgrade
                          ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-600/25 hover:shadow-lg"
                          : "border border-border bg-card text-foreground hover:border-foreground/35 hover:bg-secondary"
                      }`}
                    >
                      {isUpgrade ? <Zap className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                      {isUpgrade ? "Upgrade" : "Downgrade"}
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          {/* STEP 2 — ADD-ONS */}
          {step === 2 && (
            <div data-testid="start-step-addons">
              <p className="font-display text-lg font-black tracking-tight">Add specialty modules?</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                Tap any module to see details. Skip entirely — turn modules on later from your dashboard.
              </p>

              {/* Custom quote disclaimer */}
              <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-blue-500/25 bg-blue-500/[0.05] p-3 text-[12px]">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-blue-500" />
                <p className="leading-snug text-foreground/85">
                  <span className="font-bold">Add-on pricing is custom.</span>
                  <span className="text-muted-foreground"> Your final monthly cost is quoted after a quick 20-min discovery call. Base $297 / $497 stays fixed.</span>
                </p>
              </div>

              <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
                {ADDONS.map((m) => {
                  const Icon = m.icon;
                  const available = isAddonAvailable(m);
                  const checked = selectedAddons.has(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setDrawerAddonId(m.id)}
                      data-testid={`start-addon-${m.id}`}
                      className={`group relative flex items-start gap-3 overflow-hidden rounded-xl border p-3.5 text-left transition-all ${
                        !available
                          ? "border-dashed border-border bg-secondary/30 opacity-75 hover:opacity-100 hover:border-violet-500/40"
                          : checked
                            ? "border-violet-500/55 bg-violet-500/[0.06] shadow-sm"
                            : "border-border bg-card hover:border-foreground/25"
                      }`}
                    >
                      {!available && (
                        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-violet-600 dark:text-violet-300">
                          <Lock className="h-2.5 w-2.5" />
                          Hub+ only
                        </span>
                      )}
                      <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15 ${!available ? "grayscale-[0.4]" : ""}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1 pr-10">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[13px] font-bold tracking-tight">{m.name}</span>
                          {checked && available && (
                            <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-md bg-violet-500 text-white">
                              <Check className="h-2.5 w-2.5" />
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">{m.tagline}</p>
                      </div>
                      <ArrowRight className="absolute right-3 bottom-3 h-3 w-3 flex-none text-muted-foreground/60 transition-all group-hover:translate-x-0.5 group-hover:text-foreground/80" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3 — WHITELABEL */}
          {step === 3 && (
            <div data-testid="start-step-whitelabel">
              <p className="font-display text-lg font-black tracking-tight">Want to make it yours?</p>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                Run Clinic OS under your own brand. Optional — skip if you don&apos;t need whitelabeling.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {/* No */}
                <button
                  type="button"
                  onClick={() => setWhitelabel(false)}
                  data-testid="start-whitelabel-no"
                  className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all sm:p-5 ${
                    !whitelabel ? "border-foreground/35 bg-secondary/40 shadow-sm" : "border-border bg-card hover:border-foreground/25"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-foreground/70 ring-1 ring-inset ring-border">
                      <Check className="h-4 w-4" />
                    </span>
                    {!whitelabel && (
                      <span className="rounded-full bg-foreground px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-background">Selected</span>
                    )}
                  </div>
                  <p className="mt-3 font-display text-[15px] font-black tracking-tight">No, thanks</p>
                  <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
                    Run on the standard Clinic OS brand. Switching to whitelabel later involves a one-time migration fee.
                  </p>
                  <p className="mt-3 text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">+ $0/mo</p>
                </button>

                {/* Yes */}
                <button
                  type="button"
                  onClick={() => setWhitelabel(true)}
                  data-testid="start-whitelabel-yes"
                  className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all sm:p-5 ${
                    whitelabel ? "border-violet-500/55 bg-gradient-to-br from-violet-500/[0.10] via-fuchsia-500/[0.05] to-transparent shadow-md shadow-violet-500/10" : "border-violet-500/30 bg-gradient-to-br from-violet-500/[0.05] to-transparent hover:border-violet-500/50"
                  }`}
                >
                  <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/20 blur-3xl" />
                  <div className="relative flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-md shadow-violet-500/30">
                      <Palette className="h-4 w-4" />
                    </span>
                    {whitelabel && (
                      <span className="rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white">Selected</span>
                    )}
                  </div>
                  <p className="relative mt-3 font-display text-[15px] font-black tracking-tight">Yes — whitelabel it</p>
                  <p className="relative mt-1 text-[12px] leading-snug text-muted-foreground">
                    Your logo · your colors · your custom domain. One workspace, multiple accounts.
                  </p>
                  <p className="relative mt-3 inline-flex items-baseline gap-1">
                    <span className="font-display text-xl font-black tracking-tight">+ $297</span>
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">/mo per workspace</span>
                  </p>
                </button>
              </div>

              {whitelabel ? (
                <div className="mt-4 rounded-xl border border-violet-500/25 bg-violet-500/[0.05] p-3.5 text-[12px]">
                  <p className="leading-snug text-foreground/85">
                    <span className="font-bold">What&apos;s included:</span>
                    <span className="text-muted-foreground"> custom domain, your branding, multi-account workspace. Each clinic account inside still pays its own base subscription ($297 or $497/mo per location).</span>
                  </p>
                </div>
              ) : (
                <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3.5 text-[12px]">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-500" />
                  <p className="leading-snug text-foreground/85">
                    <span className="font-bold">Heads up — switching later isn&apos;t free.</span>
                    <span className="text-muted-foreground"> Choosing whitelabel after onboarding involves a one-time <span className="font-bold text-foreground/90">$1,500 migration charge</span> and <span className="font-bold text-foreground/90">$297/mo</span> ongoing. Pick it now and skip the migration fee.</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — SCHEDULE (with summary) */}
          {step === 4 && submitState !== "success" && (
            <div data-testid="start-step-schedule" className="grid grid-cols-1 gap-5 lg:grid-cols-[0.95fr_1.05fr] lg:gap-6">
              {/* LEFT — stack + price summary */}
              <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-secondary/60 via-secondary/30 to-transparent p-4 lg:self-start lg:sticky lg:top-0">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Your stack</p>
                <div className="mt-2 flex items-start gap-3">
                  <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${base.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                    {(() => { const I = base.icon; return <I className="h-4 w-4" />; })()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-black tracking-tight">{base.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {whitelabel && (
                        <span className="inline-flex items-center gap-1 rounded-md border border-violet-500/30 bg-violet-500/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-violet-600 dark:text-violet-300">
                          <Palette className="h-2.5 w-2.5" />
                          Whitelabel
                        </span>
                      )}
                      {Array.from(selectedAddons).map((id) => {
                        const a = addonById(id);
                        if (!a) return null;
                        return (
                          <span key={id} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[10.5px] font-semibold">
                            <span className={`h-1 w-1 rounded-full bg-gradient-to-br ${a.color}`} />
                            {a.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Price breakdown — base subscription only; add-ons quoted separately */}
                <div className="mt-3 grid grid-cols-1 gap-1.5 border-t border-border/70 pt-3 text-[12.5px] sm:grid-cols-2">
                  <div className="flex items-center justify-between sm:block">
                    <span className="text-muted-foreground">Base ({base.short})</span>
                    <span className="font-display font-black tracking-tight sm:mt-0.5 sm:block">{base.priceLabel}/mo</span>
                  </div>
                  {whitelabel && (
                    <div className="flex items-center justify-between sm:block">
                      <span className="text-muted-foreground">Whitelabel</span>
                      <span className="font-display font-black tracking-tight sm:mt-0.5 sm:block">+$297/mo</span>
                    </div>
                  )}
                  {addonCount > 0 && (
                    <div className="flex items-center justify-between sm:col-span-2 sm:block">
                      <span className="text-muted-foreground">{addonCount} add-on{addonCount > 1 ? "s" : ""} selected</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 rounded-xl border border-border bg-card/60 px-3 py-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Your minimum monthly cost</span>
                    <span className="flex items-baseline gap-0.5">
                      <span className="font-display text-2xl font-black tracking-tight">${baseTotal}</span>
                      <span className="text-[11.5px] text-muted-foreground">/mo</span>
                    </span>
                  </div>
                  {addonCount > 0 && (
                    <div className="mt-2 space-y-1">
                      {Array.from(selectedAddons).map((id) => {
                        const a = addonById(id);
                        if (!a) return null;
                        return (
                          <div key={id} className="flex items-center justify-between gap-2 text-[12px]">
                            <span className="font-semibold text-foreground/85">+ {a.name}</span>
                            <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 font-bold uppercase tracking-[0.12em] text-[10px] text-amber-700 dark:text-amber-300 whitespace-nowrap">
                              Quoted after discovery call
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-3 text-[11px] leading-snug text-muted-foreground border-t border-border/60 pt-2.5">
                    The amount above covers your base subscription only. Add-ons are sized to your clinic and quoted on the 20-minute call — no surprise charges.
                  </p>
                </div>
              </div>

              {/* RIGHT — discovery form */}
              <div>
              <p className="font-display text-lg font-black tracking-tight">Tell us about your clinic</p>
              <p className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
                A few details so we can tailor your walkthrough and prepare your add-on quote.
              </p>

              <form onSubmit={handleSubmit} className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="start-schedule-form" noValidate>
                <Field label="Name" required error={fieldErrors.name}>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="Dr. Sarah Lee"
                    data-testid="start-form-name"
                    aria-invalid={!!fieldErrors.name}
                    className={`ipt ${fieldErrors.name ? "ipt-error" : ""}`}
                  />
                </Field>
                <Field label="Clinic name" required error={fieldErrors.clinicName}>
                  <input
                    type="text" required value={form.clinicName}
                    onChange={(e) => setField("clinicName", e.target.value)}
                    placeholder="Gladstone Spine Clinic"
                    data-testid="start-form-clinic"
                    aria-invalid={!!fieldErrors.clinicName}
                    className={`ipt ${fieldErrors.clinicName ? "ipt-error" : ""}`}
                  />
                </Field>
                <Field label="Email" required error={fieldErrors.email}>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="you@clinic.com"
                    data-testid="start-form-email"
                    aria-invalid={!!fieldErrors.email}
                    className={`ipt ${fieldErrors.email ? "ipt-error" : ""}`}
                  />
                </Field>
                <Field label="Phone" optional>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    placeholder="+1 (555) 555-5555"
                    data-testid="start-form-phone"
                    className="ipt"
                  />
                </Field>
                <Field label="Current EMR" optional>
                  <select
                    value={form.emr}
                    onChange={(e) => setField("emr", e.target.value)}
                    data-testid="start-form-emr"
                    className="ipt"
                  >
                    <option value="">Select EMR…</option>
                    {EMR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Clinic type" optional>
                  <select
                    value={form.clinicType}
                    onChange={(e) => setField("clinicType", e.target.value)}
                    data-testid="start-form-type"
                    className="ipt"
                  >
                    <option value="">Select type…</option>
                    {CLINIC_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Number of Practitioners" optional>
                  <select
                    value={form.practitioners}
                    onChange={(e) => setField("practitioners", e.target.value)}
                    data-testid="start-form-practitioners"
                    className="ipt"
                  >
                    <option value="">How many?</option>
                    {PRACTITIONER_COUNTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Website URL" optional>
                  <input
                    type="url" value={form.websiteUrl}
                    onChange={(e) => setField("websiteUrl", e.target.value)}
                    placeholder="https://yourclinic.com"
                    data-testid="start-form-website"
                    className="ipt"
                  />
                </Field>

                {submitState === "error" && (
                  <p data-testid="start-form-error" className="col-span-full text-[12px] font-medium text-rose-500">{error}</p>
                )}
              </form>

              <style>{`.ipt{display:block;width:100%;border-radius:0.75rem;border-width:1px;border-color:hsl(var(--border));background-color:hsl(var(--background));padding:0.75rem 0.875rem;font-size:14px;font-weight:500;color:hsl(var(--foreground));outline:none;transition:border-color 0.15s, box-shadow 0.15s;min-height:44px}.ipt:focus{border-color:rgb(139 92 246 / 0.55);box-shadow:0 0 0 2px rgb(139 92 246 / 0.20)}.ipt-error{border-color:rgb(244 63 94 / 0.6);background-color:rgb(244 63 94 / 0.04)}.ipt-error:focus{border-color:rgb(244 63 94 / 0.8);box-shadow:0 0 0 2px rgb(244 63 94 / 0.20)}`}</style>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {step === 4 && submitState === "success" && (
            <div data-testid="start-step-success" className="text-center">
              <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/30">
                <Check className="h-7 w-7" />
              </span>
              <h3 className="mt-4 font-display text-2xl font-black tracking-tight">You&apos;re booked.</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">
                We&apos;ll reach out within 1 business day to confirm your walkthrough.
              </p>
              <div className="mx-auto mt-4 max-w-sm rounded-xl border border-border bg-secondary/40 p-3.5 text-left text-[12.5px]">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Your stack</p>
                <p className="mt-1.5 font-bold tracking-tight text-foreground">
                  {base.name} <span className="font-display font-black">{base.priceLabel}</span>
                  <span className="font-normal text-muted-foreground">/mo</span>
                </p>
                {whitelabel && (
                  <p className="mt-0.5 text-foreground/80">+ Whitelabel <span className="font-display font-black">$297</span><span className="text-muted-foreground">/mo</span></p>
                )}
                {selectedAddons.size > 0 && (
                  <p className="mt-1.5 text-foreground/80">
                    + {selectedAddons.size} add-on{selectedAddons.size > 1 ? "s" : ""} —{" "}
                    <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
                      Quoted after call
                    </span>
                  </p>
                )}
                <div className="mt-3 border-t border-border/70 pt-2.5">
                  <p className="text-[10.5px] text-muted-foreground leading-snug">
                    <span className="font-bold text-foreground">Your monthly cost is ${baseTotal}/mo</span> for the base subscription.
                    Add-on pricing is custom and quoted after your 20-minute discovery call.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer — Mobile-first, stacks vertically on mobile */}
        <div className="relative border-t border-border/70 bg-secondary/30 px-4 py-3 sm:px-6 sm:py-4">
          {/* Mobile pricing chips */}
          <div className="mb-2 flex items-center justify-between sm:hidden">
            <div className="flex items-baseline gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Base</span>
              <span className="font-display text-base font-black tracking-tight" data-testid="start-modal-total">${baseTotal}</span>
              <span className="text-[10px] text-muted-foreground">/mo</span>
            </div>
            {addonCount > 0 && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
                +{addonCount} add-on{addonCount > 1 ? "s" : ""} · quoted
              </span>
            )}
          </div>

          {/* Desktop pricing line */}
          <div className="hidden items-center justify-between sm:flex">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Your monthly cost</span>
              <span className="font-display text-xl font-black tracking-tight">${baseTotal}</span>
              <span className="text-[11px] text-muted-foreground">/mo</span>
              {whitelabel && (
                <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-violet-600 dark:text-violet-300">
                  + Whitelabel
                </span>
              )}
              {addonCount > 0 && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">
                  + {addonCount} add-on{addonCount > 1 ? "s" : ""} quoted after call
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {step > 1 && submitState !== "success" && (
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  data-testid="start-modal-back"
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-3.5 text-[12.5px] font-bold transition-colors hover:bg-secondary"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
              )}
              {step < 4 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                  data-testid={`start-modal-next-${step}`}
                  className={`inline-flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-r ${COLOR.btn} px-4 text-[12.5px] font-bold text-white shadow-md shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-lg`}
                >
                  {step === 1 ? "Continue" : step === 2 ? "Continue" : "Continue to schedule"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
              {step === 4 && submitState !== "success" && (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={submitState === "loading"}
                  data-testid="start-modal-submit"
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 text-[12.5px] font-bold text-white shadow-md shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
                >
                  {submitState === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarCheck className="h-3.5 w-3.5" />}
                  {submitState === "loading" ? "Booking…" : "Book walkthrough"}
                </button>
              )}
              {step === 4 && submitState === "success" && (
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="start-modal-done"
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-foreground px-4 text-[12.5px] font-bold text-background transition-all hover:-translate-y-0.5"
                >
                  Done
                </button>
              )}
            </div>
          </div>

          {/* Mobile nav buttons — stacked, full-width, big tap targets */}
          <div className="flex items-stretch gap-2 sm:hidden">
            {step > 1 && submitState !== "success" && (
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                data-testid="start-modal-back-mobile"
                className="inline-flex h-12 flex-none items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-4 text-[13px] font-bold transition-colors hover:bg-secondary active:scale-[0.98]"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            {step < 4 && (
              <button
                type="button"
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                data-testid={`start-modal-next-${step}-mobile`}
                className={`inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${COLOR.btn} px-4 text-[13.5px] font-bold text-white shadow-md shadow-violet-600/25 transition-all active:scale-[0.98]`}
              >
                {step === 1 ? "Continue" : step === 2 ? "Continue" : "Continue to schedule"}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 4 && submitState !== "success" && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={submitState === "loading"}
                data-testid="start-modal-submit-mobile"
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 text-[13.5px] font-bold text-white shadow-md shadow-violet-600/25 transition-all disabled:opacity-60 active:scale-[0.98]"
              >
                {submitState === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                {submitState === "loading" ? "Booking…" : "Book walkthrough"}
              </button>
            )}
            {step === 4 && submitState === "success" && (
              <button
                type="button"
                onClick={onClose}
                data-testid="start-modal-done-mobile"
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-foreground px-4 text-[13.5px] font-bold text-background transition-all active:scale-[0.98]"
              >
                Done
              </button>
            )}
          </div>
        </div>

        {/* ───────── ADD-ON DETAILS — bottom-sheet on mobile, right-drawer on desktop ───────── */}
        <AnimatePresence>
          {drawerAddon && (() => {
            const Icon = drawerAddon.icon;
            const available = isAddonAvailable(drawerAddon);
            const checked = selectedAddons.has(drawerAddon.id);
            return (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="absolute inset-0 z-10 bg-foreground/40 backdrop-blur-[1px]"
                  onClick={() => setDrawerAddonId(null)}
                />
                {/* Mobile: bottom-sheet */}
                <motion.aside
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="addon-drawer"
                  className="absolute inset-x-0 bottom-0 z-20 flex max-h-[88%] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-card shadow-2xl sm:hidden"
                >
                  <div className={`h-[3px] w-full bg-gradient-to-r ${drawerAddon.color}`} />
                  <span aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-full bg-foreground/15" />

                  <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${drawerAddon.color} text-white`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <p className="truncate font-display text-[14px] font-bold tracking-tight">{drawerAddon.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDrawerAddonId(null)}
                      data-testid="addon-drawer-close"
                      aria-label="Close"
                      className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${drawerAddon.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-3 font-display text-xl font-black tracking-tight">{drawerAddon.name}</h3>
                    <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{drawerAddon.tagline}</p>
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-2.5 text-[11.5px]">
                      <Info className="mt-0.5 h-3 w-3 flex-none text-amber-500" />
                      <p className="leading-snug text-foreground/85">
                        <span className="font-bold">Pricing is custom.</span> Quoted after a 20-min discovery call.
                      </p>
                    </div>

                    {!available && (
                      <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-violet-500/30 bg-violet-500/10 p-3 text-[12px]">
                        <Lock className="mt-0.5 h-3.5 w-3.5 flex-none text-violet-600 dark:text-violet-300" />
                        <div className="min-w-0">
                          <p className="font-bold text-violet-700 dark:text-violet-200">Requires Hub+ ($497/mo)</p>
                          <p className="mt-0.5 text-violet-600/85 dark:text-violet-300/85">
                            We&apos;ll upgrade your base and add this in one step.
                          </p>
                        </div>
                      </div>
                    )}

                    <p className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">What you get</p>
                    <ul className="mt-2 space-y-2">
                      {drawerAddon.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[12.5px] leading-snug text-foreground/85">
                          <Check className={`mt-0.5 h-3.5 w-3.5 flex-none ${drawerAddon.accent}`} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-border/70 bg-secondary/30 p-3">
                    {!available ? (
                      <button
                        type="button"
                        onClick={handleAddFromDrawer}
                        data-testid="addon-drawer-upgrade"
                        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-[13px] font-bold text-white shadow-md shadow-violet-600/25 active:scale-[0.98]"
                      >
                        <Zap className="h-4 w-4" />
                        Upgrade to Hub+ and add this
                      </button>
                    ) : checked ? (
                      <button
                        type="button"
                        onClick={handleRemoveFromDrawer}
                        data-testid="addon-drawer-remove"
                        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card text-[13px] font-bold text-foreground active:scale-[0.98]"
                      >
                        <X className="h-4 w-4" />
                        Remove from my plan
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleAddFromDrawer}
                        data-testid="addon-drawer-add"
                        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-foreground text-[13px] font-bold text-background active:scale-[0.98]"
                      >
                        <Check className="h-4 w-4" />
                        Add to my plan
                      </button>
                    )}
                  </div>
                </motion.aside>

                {/* Desktop: right-drawer */}
                <motion.aside
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="addon-drawer-desktop"
                  className="absolute right-0 top-0 z-20 hidden h-full w-full max-w-md flex-col overflow-hidden border-l border-border bg-card shadow-2xl sm:flex"
                >
                  <div className={`h-[3px] w-full bg-gradient-to-r ${drawerAddon.color}`} />
                  <div className="flex items-center justify-between border-b border-border/70 px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${drawerAddon.color} text-white`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <p className="font-display text-[14px] font-bold tracking-tight">{drawerAddon.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDrawerAddonId(null)}
                      aria-label="Close"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-5">
                    <span className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${drawerAddon.color} text-white shadow-md ring-1 ring-inset ring-white/15`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-4 font-display text-2xl font-black tracking-tight">{drawerAddon.name}</h3>
                    <p className="mt-1.5 text-[14px] leading-snug text-muted-foreground">{drawerAddon.tagline}</p>

                    <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-[12px]">
                      <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-500" />
                      <p className="leading-snug text-foreground/85">
                        <span className="font-bold">Pricing is custom.</span> Quoted after a 20-min discovery call.
                      </p>
                    </div>

                    {!available && (
                      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-violet-500/30 bg-violet-500/10 p-3.5 text-[12.5px]">
                        <Lock className="mt-0.5 h-3.5 w-3.5 flex-none text-violet-600 dark:text-violet-300" />
                        <div className="min-w-0">
                          <p className="font-bold text-violet-700 dark:text-violet-200">Requires Clinic Hub+ ($497/mo)</p>
                          <p className="mt-0.5 text-violet-600/85 dark:text-violet-300/85">
                            This module needs Hub+&apos;s Automations engine. You can upgrade your base now and add it in one step.
                          </p>
                        </div>
                      </div>
                    )}

                    <p className="mt-5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">What you get</p>
                    <ul className="mt-2 space-y-2.5">
                      {drawerAddon.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-[13px] leading-snug text-foreground/85">
                          <Check className={`mt-0.5 h-4 w-4 flex-none ${drawerAddon.accent}`} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-border/70 bg-secondary/30 px-5 py-4">
                    {!available ? (
                      <button
                        type="button"
                        onClick={handleAddFromDrawer}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-[13px] font-bold text-white shadow-md shadow-violet-600/25"
                      >
                        <Zap className="h-3.5 w-3.5" />
                        Upgrade to Hub+ and add this
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    ) : checked ? (
                      <button
                        type="button"
                        onClick={handleRemoveFromDrawer}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card text-[13px] font-bold text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                        Remove from my plan
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleAddFromDrawer}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-foreground text-[13px] font-bold text-background"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Add to my plan
                      </button>
                    )}
                  </div>
                </motion.aside>
              </>
            );
          })()}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* Field wrapper for the schedule form */
const Field = ({ label, optional, required, error, children }) => (
  <div>
    <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {label}
      {optional && <span className="ml-1 font-normal lowercase tracking-normal text-muted-foreground/60">(optional)</span>}
      {required && <span className="ml-1 text-rose-500">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
    {error && (
      <p className="mt-1 text-[11px] font-medium text-rose-500">{error}</p>
    )}
  </div>
);
